import { differenceInDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

export const SEC_MS = 1000;
export const MINUTE_SECS = 60;
const DAY_HOURS = 24;
const WEEK_DAYS = 7;
const MONTH_DAYS = 30.5;
const YEAR_DAYS = 365.25;

const HOUR_SECS = MINUTE_SECS * MINUTE_SECS;
const DAY_SECS = DAY_HOURS * HOUR_SECS;
const WEEK_SECS = WEEK_DAYS * DAY_SECS;
const MONTH_SECS = MONTH_DAYS * DAY_SECS;
const YEAR_SECS = YEAR_DAYS * DAY_SECS;

interface RelativeTimeDestructure {
  range: Intl.RelativeTimeFormatUnit;
  value: number;
}

export const relativeTimeDestructure = (date: number, relative: number = Date.now()): RelativeTimeDestructure => {
  const seconds = (date - relative) / SEC_MS;
  const oom = Math.abs(seconds);

  if (oom < MINUTE_SECS) {
    return {
      range: 'second',
      value: Math.round(seconds),
    };
  } else if (oom < HOUR_SECS) {
    return {
      range: 'minute',
      value: Math.round(seconds / MINUTE_SECS),
    };
  } else if (oom < DAY_SECS) {
    return {
      range: 'hour',
      value: Math.round(seconds / HOUR_SECS),
    };
  } else if (oom < WEEK_SECS) {
    return {
      range: 'day',
      value: Math.round(seconds / DAY_SECS),
    };
  } else if (oom < MONTH_SECS) {
    return {
      range: 'week',
      value: Math.round(seconds / WEEK_SECS),
    };
  } else if (oom < YEAR_SECS) {
    return {
      range: 'month',
      value: Math.round(seconds / MONTH_SECS),
    };
  }

  return {
    range: 'year',
    value: Math.round(seconds / YEAR_SECS),
  };
};

export const tryParseDate = (date?: string) => (date ? new Date(date) : undefined);

export const formatDate = (string: string) => {
  try {
    return formatInTimeZone(Date.parse(string), 'Europe/Amsterdam', 'dd MMM yyyy', {
      locale: nl,
    });
  } catch {
    return '';
  }
};

export const formatDateTime = (string: string, format?: string) => {
  try {
    return formatInTimeZone(Date.parse(string), 'Europe/Amsterdam', format ?? 'dd MMM yyyy, HH:mm', {
      locale: nl,
    });
  } catch {
    return '';
  }
};

export const formatTime = (string: string, format?: string) => {
  try {
    return formatInTimeZone(string, 'Europe/Amsterdam', format ?? 'HH:mm', {
      locale: nl,
    });
  } catch {
    return '';
  }
};

export const formatDateOrTime = (dateString: string) => {
  const date = Date.parse(dateString);
  if (differenceInDays(date, new Date()) === 0) {
    return formatTime(dateString);
  }
  try {
    return formatInTimeZone(date, 'Europe/Amsterdam', 'dd MMM yyyy', {
      locale: nl,
    });
  } catch {
    return '';
  }
};

export const formatDateRange = (fromString: string, toString?: string) => {
  if (!toString) {
    return formatDateTime(fromString);
  }

  const from = Date.parse(fromString);
  const to = Date.parse(toString);

  if (differenceInDays(from, to) === 0) {
    return `${formatDateTime(fromString)}-${formatTime(toString)}`;
  }

  return `${formatDate(fromString)} t/m ${formatDate(toString)}`;
};
